import React, { useEffect, useRef, useState } from "react";
import { CheckIcon, LockClosedIcon } from "@heroicons/react/20/solid";

// One training run, told as it actually settles: nothing billed before the
// dataset checks out, then a slice at a time, each checkpoint in the client's
// hands before the host is paid for it.
const SLICES = 4;

const frames = [
  {
    label: "Before any GPU time",
    title: "Your dataset, sealed",
    desc: "Split into shards and encrypted on your own machine. The storage network holds the pieces and never a key.",
    slices: 0,
    dur: 3200,
  },
  {
    label: "Before any GPU time",
    title: "Checked before a cycle is spent",
    desc: "Every shard verified against its hash, the content scanned, and the tokens re-counted against the number you declared.",
    slices: 0,
    dur: 3600,
  },
  {
    label: "Slice 1 of 4",
    title: "Checkpoint first, payment after",
    desc: "The encrypted checkpoint reaches you before the host is paid for it. Money never moves ahead of the work.",
    slices: 1,
    dur: 2800,
  },
  {
    label: "Slice 2 of 4",
    title: "Settled slice by slice",
    desc: "Each slice is priced, proved and paid on its own, at the rate that was agreed before the run started.",
    slices: 2,
    dur: 2600,
  },
  {
    label: "Slice 3 of 4",
    title: "Stop whenever you like",
    desc: "Cancel here and you are billed for three slices. Not for the run you did not get.",
    slices: 3,
    dur: 2800,
  },
  {
    label: "Slice 4 of 4",
    title: "The run completes",
    desc: "Four slices done, four slices paid, and every one of them already in your hands.",
    slices: 4,
    dur: 2400,
  },
  {
    label: "Delivered",
    title: "Your model comes back sealed",
    desc: "The finished model returns encrypted, under keys only you hold, and attaches to a chat that stays private to that session alone.",
    slices: 4,
    done: true,
    dur: 4600,
  },
];

export default function TrainingRunAnimation() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setIndex(frames.length - 1);
      return;
    }
    timerRef.current = setTimeout(
      () => setIndex((i) => (i + 1) % frames.length),
      frames[index].dur
    );
    return () => clearTimeout(timerRef.current);
  }, [index]);

  const frame = frames[index];

  return (
    <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 sm:p-8">
      <div key={index} className="min-h-[7.5rem] animate-[training-fade_0.4s_ease] sm:min-h-[6.5rem]">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-lightpurple">
          {frame.label}
        </p>
        <p className="mt-2 text-lg font-semibold text-white">{frame.title}</p>
        <p className="mt-1 text-sm/6 text-gray-300">{frame.desc}</p>
      </div>

      <div className="mt-6 flex items-center justify-between text-xs text-brand-gray">
        <span className="inline-flex items-center gap-1.5">
          <LockClosedIcon aria-hidden="true" className="size-3.5" />
          Sealed on your machine
        </span>
        <span
          className={`inline-flex items-center gap-1.5 transition-opacity duration-500 ${
            frame.done ? "opacity-100 text-brand-pink" : "opacity-40"
          }`}
        >
          <LockClosedIcon aria-hidden="true" className="size-3.5" />
          Sealed back to you
        </span>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {Array.from({ length: SLICES }, (_, i) => {
          const paid = i < frame.slices;
          const running = i === frame.slices && !frame.done;
          return (
            <div
              key={i}
              className={`flex h-11 items-center justify-center rounded-lg text-xs font-semibold ring-1 transition-all duration-500 ${
                paid
                  ? "bg-gradient-to-r from-brand-purple to-brand-pink text-white ring-brand-pink/50"
                  : running
                  ? "bg-white/5 text-brand-lightpurple ring-brand-lightpurple/60"
                  : "bg-white/5 text-brand-gray/60 ring-white/10"
              }`}
            >
              {paid ? (
                <span className="inline-flex items-center gap-1">
                  <CheckIcon aria-hidden="true" className="size-4" />
                  Paid
                </span>
              ) : running ? (
                "Training"
              ) : (
                `Slice ${i + 1}`
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-center text-sm text-brand-gray" aria-live="polite">
        {frame.slices === 0
          ? "Nothing billed yet"
          : `Paid for ${frame.slices} of ${SLICES} slices`}
      </p>

      <style>{`
        @keyframes training-fade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}
